import React, { useState, useEffect, Suspense } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  FaChalkboardTeacher, 
  FaUserGraduate, 
  FaBook, 
  FaTachometerAlt, 
  FaCalendarAlt, 
  FaTasks, 
  FaCog, 
  FaBell, 
  FaSignOutAlt, 
  FaUserCircle,
  FaInbox,
  FaChartLine,
  FaMoneyBillWave,
  FaFileAlt,
  FaCrown,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationTriangle
} from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import TeacherRoutes from '../../routes/teacherRoutes';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Error Boundary for handling errors in TeacherPanel
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error in TeacherPanel:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gray-100">
          <FaExclamationTriangle className="text-yellow-500 text-5xl mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-6 text-center max-w-md">
            We encountered an error loading the teacher dashboard. This might be due to a connection issue or a recent update.
          </p>
          <div className="flex space-x-4">
            <button 
              className="bg-primary text-white px-4 py-2 rounded-md"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </button>
            <button 
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md"
              onClick={() => {
                // Clear localStorage and redirect to login
                localStorage.removeItem('token');
                window.location.href = '/login';
              }}
            >
              Back to Login
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * TeacherPanel component - Layout wrapper for all teacher pages
 */
const TeacherPanel = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [hasLoadingError, setHasLoadingError] = useState(false);

  // Add error listener for chunk loading errors
  useEffect(() => {
    const handleError = (event) => {
      console.error('Caught error:', event);
      // Check if it's a chunk loading error
      if (event.error && event.error.message && event.error.message.includes('ChunkLoadError')) {
        setHasLoadingError(true);
        // Prevent the default error handling
        event.preventDefault();
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, []);

  // Check authentication on component mount
  useEffect(() => {
    if (!isAuthenticated || (user && user.role !== 'teacher')) {
      navigate('/login', { state: { message: 'Please log in as a teacher to access this page' } });
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!isAuthenticated || !user) return;
      try {
        // Replace with actual endpoint when available
        // const res = await api.get('/teacher/notifications');
        // setNotifications(res?.data?.data || []);
        setNotifications([]);
      } catch (err) {
        setNotifications([]);
      }
    };
    fetchNotifications();
  }, [isAuthenticated, user]);

  // Handle notification click
  const handleNotificationClick = (notificationId) => {
    // Mark notification as read
    setNotifications(prevNotifications => 
      prevNotifications.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true } 
          : notification
      )
    );
    // Close notification dropdown
    setIsNotificationOpen(false);
  };

  // Format date for display
  const formatDate = (date) => {
    const now = new Date();
    const diff = now - date;
    
    // Less than a day
    if (diff < 86400000) {
      // Less than an hour
      if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000);
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
      }
      
      const hours = Math.floor(diff / 3600000);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }
    
    // Less than a week
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }
    
    return date.toLocaleDateString();
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
    showToast('Logged out successfully', 'success');
  };

  // Navigation links for the sidebar
  const navigationLinks = [
    { name: 'Dashboard', to: '/teacher', icon: <FaTachometerAlt className="mr-3 h-5 w-5" /> },
    { name: 'Classes', to: '/teacher/classes', icon: <FaChalkboardTeacher className="mr-3 h-5 w-5" /> },
    { name: 'Students', to: '/teacher/students', icon: <FaUserGraduate className="mr-3 h-5 w-5" /> },
    { name: 'Assignments', to: '/teacher/assignments', icon: <FaTasks className="mr-3 h-5 w-5" /> },
    { name: 'Calendar', to: '/teacher/calendar', icon: <FaCalendarAlt className="mr-3 h-5 w-5" /> },
    { name: 'Resources', to: '/teacher/resources', icon: <FaBook className="mr-3 h-5 w-5" /> },
    { name: 'Student Progress', to: '/teacher/progress', icon: <FaChartLine className="mr-3 h-5 w-5" /> },
    { name: 'Messages', to: '/teacher/messages', icon: <FaInbox className="mr-3 h-5 w-5" /> },
    { name: 'Reports', to: '/teacher/reports', icon: <FaFileAlt className="mr-3 h-5 w-5" /> },
    { name: 'Settings', to: '/teacher/settings', icon: <FaCog className="mr-3 h-5 w-5" /> }
  ];

  const toggleSidebarSize = () => {
    setIsSidebarMinimized(!isSidebarMinimized);
  };

  // Handle page reload due to error
  const handlePageReload = () => {
    window.location.reload();
  };

  // If loading error occurred, show error screen
  if (hasLoadingError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gray-100">
        <FaExclamationTriangle className="text-yellow-500 text-5xl mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Page Loading Error</h2>
        <p className="text-gray-600 mb-6 text-center max-w-md">
          We encountered an error loading the teacher dashboard. This might be due to a connection issue or a recent update.
        </p>
        <div className="flex space-x-4">
          <button 
            className="bg-primary text-white px-4 py-2 rounded-md"
            onClick={handlePageReload}
          >
            Reload Page
          </button>
          <button 
            className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md"
            onClick={() => {
              localStorage.removeItem('token');
              window.location.href = '/login';
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // If not authenticated yet, show loading
  if (!isAuthenticated) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Helmet>
        <title>Teacher Portal | Meira</title>
        <meta name="description" content="Teacher portal for managing classes, assignments, students, and resources." />
      </Helmet>

      <div className="h-screen flex overflow-hidden bg-gray-100">
        {/* Mobile sidebar overlay */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 z-40 bg-gray-600 bg-opacity-75"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}

        {/* Sidebar */}
        <div 
          className={`${
            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          } md:translate-x-0 fixed inset-y-0 left-0 flex flex-col z-50 ${
            isSidebarMinimized ? 'md:w-16' : 'md:w-64'
          } transition-all duration-300 transform bg-primary text-white md:static md:inset-0 md:translate-x-0`}
        >
          <div className="flex items-center justify-between flex-shrink-0 px-4 py-4 bg-primary-dark">
            <Link to="/teacher" className="flex items-center">
              <img className="h-8 w-auto" src="https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/meira_logo_design_transparent_cj7blv.png" alt="Meira Logo" />
              {!isSidebarMinimized && <span className="ml-2 text-xl font-semibold">Meira Teacher</span>}
            </Link>
            <div className="flex items-center">
              <button
                className="hidden md:block rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white mr-1"
                onClick={toggleSidebarSize}
              >
                {isSidebarMinimized ? <FaChevronRight /> : <FaChevronLeft />}
              </button>
              <button
                className="md:hidden rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="overflow-y-auto flex-grow">
            <nav className={`mt-5 ${isSidebarMinimized ? 'px-1' : 'px-2'} space-y-1`}>
              {navigationLinks.map((link) => {
                const isActive = location.pathname === link.to || 
                  (link.to !== '/teacher' && location.pathname.startsWith(link.to));
                
                return (
                  <Link
                    key={link.name}
                    to={link.to}
                    className={`${
                      isActive
                        ? 'bg-primary-dark text-white'
                        : 'text-white hover:bg-primary-light'
                    } group flex items-center ${isSidebarMinimized ? 'justify-center px-1' : 'px-2'} py-2 text-base font-medium rounded-md transition-colors`}
                    title={isSidebarMinimized ? link.name : ''}
                  >
                    <div className={isSidebarMinimized ? 'mx-auto' : ''}>
                      {React.cloneElement(link.icon, { className: isSidebarMinimized ? 'h-5 w-5' : link.icon.props.className })}
                    </div>
                    {!isSidebarMinimized && <span>{link.name}</span>}
                  </Link>
                );
              })}
            </nav>
          </div>
          
          <div className="flex-shrink-0 p-4 border-t border-primary-dark">
            <div className={`flex ${isSidebarMinimized ? 'justify-center' : 'items-center'}`}>
              {!isSidebarMinimized && (
                <>
                  <div>
                    <FaUserCircle className="h-9 w-9 text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-white">{user?.name || 'Teacher'}</p>
                    <button
                      onClick={handleLogout}
                      className="text-xs text-gray-300 hover:text-white flex items-center"
                    >
                      <FaSignOutAlt className="mr-1 h-3 w-3" />
                      Sign out
                    </button>
                  </div>
                </>
              )}
              {isSidebarMinimized && (
                <button
                  onClick={handleLogout}
                  className="text-white hover:text-gray-300"
                  title="Sign out"
                >
                  <FaSignOutAlt className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-col w-0 flex-1 overflow-hidden">
          <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
            <button
              type="button"
              className="px-4 md:hidden"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <svg className="h-6 w-6 text-gray-500" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <div className="flex-1 px-4 flex justify-end">
              <div className="ml-4 flex items-center md:ml-6">
                {/* Notification bell */}
                <div className="relative ml-3">
                  <button
                    className="relative p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500"
                    onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                  >
                    <FaBell className="h-6 w-6" />
                    {notifications.filter(n => !n.read).length > 0 && (
                      <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white"></span>
                    )}
                  </button>
                  
                  {/* Notification dropdown */}
                  {isNotificationOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        <div className="px-4 py-2 border-b border-gray-200">
                          <h3 className="text-sm font-medium text-gray-900">Notifications</h3>
                        </div>
                        
                        {notifications.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            No new notifications
                          </div>
                        ) : (
                          <div className="max-h-60 overflow-y-auto">
                            {notifications.map((notification) => (
                              <button
                                key={notification.id}
                                className={`px-4 py-3 w-full text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                                  notification.read ? 'bg-white' : 'bg-blue-50'
                                }`}
                                onClick={() => handleNotificationClick(notification.id)}
                              >
                                <p className={`text-sm ${notification.read ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {formatDate(notification.date)}
                                </p>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        <div className="px-4 py-2 border-t border-gray-200">
                          <Link
                            to="/teacher/notifications"
                            className="text-xs text-primary font-medium hover:text-primary-dark"
                            onClick={() => setIsNotificationOpen(false)}
                          >
                            View all notifications
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* User menu */}
                <div className="ml-3 relative">
                  <button
                    className="max-w-xs flex items-center text-sm rounded-full text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  >
                    <span className="sr-only">Open user menu</span>
                    <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {user?.name?.charAt(0) || 'T'}
                      </span>
                    </div>
                  </button>
                  
                  {/* User dropdown */}
                  {isUserMenuOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        <Link
                          to="/teacher/profile"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Your Profile
                        </Link>
                        <Link
                          to="/teacher/settings"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Settings
                        </Link>
                        <button
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={handleLogout}
                        >
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Page content */}
          <main className="flex-1 relative overflow-y-auto focus:outline-none bg-gray-100">
            <div className="py-6">
              <Suspense fallback={
                <div className="flex justify-center items-center h-64">
                  <LoadingSpinner size="lg" />
                </div>
              }>
                <TeacherRoutes />
              </Suspense>
            </div>
          </main>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default TeacherPanel;
